import { useEffect, useState } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { api, fmt$, STATUS_COLORS, PAYMENT_STATUS_COLORS, APPT_STATUS_FILTERS, PAYMENT_STATUS_FILTERS, loadUser, logout, API } from "@/lib/api";
import { toast } from "sonner";
import { LayoutDashboard, ClipboardList, Users, Settings, Tag, MessageSquare, LogOut, Zap, Calendar, X as XIcon, DollarSign, Trophy, Download, KeyRound, Camera, Trash2, Clock, ChevronDown, ChevronUp, Ban, Receipt } from "lucide-react";
import AdminCalendar from "@/pages/AdminCalendar";
import { Avatar, fileToCompressedDataUri } from "@/components/site/Avatar";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function AdminNav() {
  const loc = useLocation();
  const items = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, tid: "admin-nav-dashboard" },
    { to: "/admin/calendar", label: "Calendar", icon: Calendar, tid: "admin-nav-calendar" },
    { to: "/admin/bookings", label: "Booking Requests", icon: ClipboardList, tid: "admin-nav-bookings" },
    { to: "/admin/services", label: "Services", icon: Settings, tid: "admin-nav-services" },
    { to: "/admin/employees", label: "Employees", icon: Users, tid: "admin-nav-employees" },
    { to: "/admin/promo-codes", label: "Promo Codes", icon: Tag, tid: "admin-nav-promos" },
    { to: "/admin/tip-payouts", label: "Tip Payouts", icon: DollarSign, tid: "admin-nav-tips" },
    { to: "/admin/group-inquiries", label: "Group Inquiries", icon: MessageSquare, tid: "admin-nav-group" },
  ];
  return (
    <aside className="w-64 shrink-0 border-r border-white/10 min-h-screen p-4 hidden md:block">
      <Link to="/" className="flex items-center gap-2 px-3 py-4">
        <Zap className="text-[#CCFF00]" size={20} />
        <span className="font-display text-lg">NGS Admin</span>
      </Link>
      <nav className="mt-2 space-y-1">
        {items.map((i) => {
          const active = i.to === "/admin" ? loc.pathname === "/admin" : loc.pathname.startsWith(i.to);
          return (
            <Link key={i.to} to={i.to} data-testid={i.tid}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${active ? "bg-[#CCFF00]/10 text-[#CCFF00]" : "text-zinc-300 hover:bg-zinc-800"}`}>
              <i.icon size={16} /> {i.label}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setD(r.data));
    api.get("/admin/referral-leaderboard").then((r) => setLeaderboard(r.data.top || []));
  }, []);
  if (!d) return <div className="text-zinc-400">Loading...</div>;
  const kpis = [
    { label: "Requests Need Action", value: d.pending_requests + d.proposed_awaiting, hot: (d.pending_requests + d.proposed_awaiting) > 0, tid: "kpi-action" },
    { label: "Today's Appointments", value: d.today_appointments.length, tid: "kpi-today" },
    { label: "Revenue Collected", value: fmt$(d.revenue), tid: "kpi-revenue" },
    { label: "Deposits Collected", value: fmt$(d.deposits), tid: "kpi-deposits" },
    { label: "Tips Received", value: fmt$(d.tips), tid: "kpi-tips" },
    { label: "Outstanding", value: fmt$(d.outstanding), tid: "kpi-outstanding" },
    { label: "New Group Inquiries", value: d.group_inquiries_new, tid: "kpi-group" },
  ];
  return (
    <div>
      <h1 className="font-display text-4xl mb-6">Dashboard</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} data-testid={k.tid}
            className={`card-surface p-5 border-l-4 ${k.hot ? "border-l-orange-400" : "border-l-[#CCFF00]"}`}>
            <div className="text-xs uppercase tracking-widest text-zinc-400">{k.label}</div>
            <div className={`font-display text-3xl mt-2 ${k.hot ? "text-orange-300" : ""}`}>{k.value}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-surface p-6">
          <div className="font-display text-2xl mb-3">Today</div>
          {d.today_appointments.length === 0 && <div className="text-zinc-500">Nothing scheduled today.</div>}
          {d.today_appointments.map((b) => (
            <div key={b.id} className="border-t border-zinc-800 pt-3 mt-3 text-sm">
              <div className="font-semibold">{b.confirmed_time} · {b.customer_name}</div>
              <div className="text-zinc-400 text-xs">{b.totals?.service_name} · {b.address}</div>
            </div>
          ))}
        </div>
        <div className="card-surface p-6">
          <div className="font-display text-2xl mb-3">Upcoming</div>
          {d.upcoming_appointments.slice(0, 6).map((b) => (
            <div key={b.id} className="border-t border-zinc-800 pt-3 mt-3 text-sm">
              <div className="font-semibold">{b.confirmed_date} {b.confirmed_time} · {b.customer_name}</div>
              <div className="text-zinc-400 text-xs">{b.totals?.service_name} · {b.address}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 card-surface p-6" data-testid="referral-leaderboard-card">
        <div className="flex items-center gap-2 font-display text-2xl mb-4">
          <Trophy className="text-[#CCFF00]" size={20} /> Top Referrers
        </div>
        {leaderboard.length === 0 ? (
          <div className="text-zinc-500 text-sm">No referrals yet — share the love and someone will show up here.</div>
        ) : (
          <div className="space-y-2">
            {leaderboard.map((r, i) => (
              <div key={r.email} data-testid={`leaderboard-row-${i}`}
                className="flex items-center gap-3 py-2 border-b border-zinc-800 last:border-b-0">
                <div className="w-8 h-8 rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]/40 flex items-center justify-center text-[#CCFF00] font-mono text-xs">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{r.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{r.email} · <span className="font-mono">{r.code}</span></div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl">{r.referrals}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Referrals</div>
                </div>
                <div className="text-right w-24">
                  <div className="text-[#CCFF00] font-mono">{fmt$(r.credit_balance)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Credit</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TipPayouts() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/tip-payouts").then((r) => setData(r.data)); }, []);
  const downloadCsv = async () => {
    const token = localStorage.getItem("ngs_token");
    const res = await fetch(`${API}/admin/tip-payouts.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `tip-payouts-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };
  if (!data) return <div className="text-zinc-400">Loading...</div>;
  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-display text-4xl">Tip Payouts</h1>
        <button data-testid="tip-export-csv-btn" onClick={downloadCsv}
          className="neon-btn px-5 py-3 rounded-xl inline-flex items-center gap-2 text-sm">
          <Download size={16} /> Export CSV
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="card-surface p-5" data-testid="tip-kpi-total">
          <div className="text-xs uppercase tracking-widest text-zinc-400">Total tips</div>
          <div className="font-display text-3xl text-[#CCFF00] mt-2">{fmt$(data.grand_total)}</div>
        </div>
        <div className="card-surface p-5" data-testid="tip-kpi-count">
          <div className="text-xs uppercase tracking-widest text-zinc-400">Tips received</div>
          <div className="font-display text-3xl mt-2">{data.tip_count}</div>
        </div>
        <div className="card-surface p-5" data-testid="tip-kpi-detailers">
          <div className="text-xs uppercase tracking-widest text-zinc-400">Detailers tipped</div>
          <div className="font-display text-3xl mt-2">{data.employees.length}</div>
        </div>
      </div>
      <div className="space-y-4">
        {data.employees.length === 0 && <div className="text-zinc-500">No tips recorded yet.</div>}
        {data.employees.map((emp) => (
          <div key={emp.employee_id} className="card-surface p-5" data-testid={`tip-emp-${emp.employee_id}`}>
            <div className="flex justify-between items-start flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Avatar src={emp.employee_photo} name={emp.employee_name} size={48} />
                <div>
                  <div className="font-display text-xl">{emp.employee_name}</div>
                  <div className="text-xs text-zinc-500">{emp.employee_email} · {emp.tip_count} tip{emp.tip_count === 1 ? "" : "s"}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-3xl text-[#CCFF00]">{fmt$(emp.tip_total)}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Payout due</div>
              </div>
            </div>
            <div className="mt-3 divide-y divide-zinc-800">
              {emp.tips.map((t) => (
                <div key={t.booking_id} className="py-2 flex justify-between text-sm">
                  <div>
                    <div>{t.customer_name} · <span className="text-zinc-500">{t.service_name}</span></div>
                    <div className="text-xs text-zinc-500">{t.date}</div>
                  </div>
                  <div className="font-mono text-[#CCFF00]">{fmt$(t.amount)}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Bookings() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [detailers, setDetailers] = useState([]);
  const [modal, setModal] = useState(null);
  const [assign, setAssign] = useState({ employee_ids: [], accept_time: true, proposed_date: "", proposed_time: "", waive_deposit: false, waive_reason: "" });
  const [expanded, setExpanded] = useState({}); // {bookingId: true}
  const [history, setHistory] = useState({}); // {bookingId: {manual_payments, events, payment}}
  const [recordFor, setRecordFor] = useState(null); // booking id
  const [record, setRecord] = useState({ amount: "", method: "Cash", method_other: "", note: "" });
  const [waiveFor, setWaiveFor] = useState(null);
  const [waiveReason, setWaiveReason] = useState("");
  const [apptFilter, setApptFilter] = useState("");
  const [payFilter, setPayFilter] = useState("");

  const load = () => api.get("/admin/bookings").then((r) => setRows(r.data));
  const loadSummary = () => api.get("/admin/payments/summary").then((r) => setSummary(r.data));
  const loadDetailers = async () => {
    const [emps, admins] = await Promise.all([
      api.get("/admin/employees").then((r) => r.data),
      api.get("/admin/admins").then((r) => r.data),
    ]);
    const admDet = (admins || []).filter((a) => a.is_detailer && a.active !== false);
    setDetailers([...(emps || []).filter((e) => e.active !== false), ...admDet]);
  };
  useEffect(() => { load(); loadDetailers(); loadSummary(); }, []);

  const openAssign = (b) => {
    setModal(b);
    const preselected = b.assigned_employee_ids && b.assigned_employee_ids.length
      ? b.assigned_employee_ids
      : (detailers[0] ? [detailers[0].id] : []);
    setAssign({ employee_ids: preselected, accept_time: true,
                proposed_date: b.preferred_date, proposed_time: b.preferred_time,
                waive_deposit: false, waive_reason: "" });
  };
  const toggleDetailer = (id) => {
    setAssign((a) => ({
      ...a,
      employee_ids: a.employee_ids.includes(id)
        ? a.employee_ids.filter((x) => x !== id)
        : [...a.employee_ids, id],
    }));
  };
  const submitAssign = async (waive) => {
    if (!assign.employee_ids || assign.employee_ids.length === 0) {
      toast.error("Select at least one detailer"); return;
    }
    try {
      await api.post(`/admin/bookings/${modal.id}/assign`, { ...assign, waive_deposit: !!waive });
      toast.success(waive ? "Approved & deposit waived" : "Approved & deposit requested");
      setModal(null); load(); loadSummary();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const updateStatus = async (id, status) => {
    await api.post(`/admin/bookings/${id}/status`, { status });
    toast.success(status); load(); loadSummary();
  };

  const openWaive = (bid) => { setWaiveFor(bid); setWaiveReason(""); };
  const confirmWaive = async () => {
    try {
      await api.post(`/admin/bookings/${waiveFor}/waive-deposit`, { reason: waiveReason });
      toast.success("Deposit waived");
      setWaiveFor(null); load(); loadSummary();
      // Refresh history for this booking if expanded
      if (expanded[waiveFor]) loadHistory(waiveFor);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const openRecord = (bid) => { setRecordFor(bid); setRecord({ amount: "", method: "Cash", method_other: "", note: "" }); };
  const submitRecord = async () => {
    const amt = Number(record.amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount greater than $0"); return; }
    if (record.method === "Other" && !record.method_other.trim()) {
      toast.error("Describe the payment method"); return;
    }
    try {
      await api.post(`/admin/bookings/${recordFor}/payments`, {
        amount: amt, method: record.method, method_other: record.method_other,
        note: record.note,
      });
      toast.success("Payment recorded");
      setRecordFor(null); load(); loadSummary();
      if (expanded[recordFor]) loadHistory(recordFor);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const loadHistory = async (bid) => {
    const { data } = await api.get(`/admin/bookings/${bid}/payment-history`);
    setHistory((h) => ({ ...h, [bid]: data }));
  };
  const toggleExpand = (bid) => {
    const nowExpanded = !expanded[bid];
    setExpanded((e) => ({ ...e, [bid]: nowExpanded }));
    if (nowExpanded) loadHistory(bid);
  };

  const voidPayment = async (bid, pid) => {
    const reason = window.prompt("Reason for voiding this payment? (optional)") || "";
    if (reason === null) return;
    try {
      await api.post(`/admin/bookings/${bid}/payments/${pid}/void`, { reason });
      toast.success("Payment voided");
      load(); loadSummary(); loadHistory(bid);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const filtered = rows.filter((b) => {
    if (apptFilter === "__upcoming") {
      const today = new Date().toISOString().slice(0, 10);
      if (!b.confirmed_date || b.confirmed_date < today) return false;
      if (!["Confirmed", "Awaiting Deposit", "In Progress"].includes(b.status)) return false;
    } else if (apptFilter && b.status !== apptFilter) return false;
    if (payFilter === "__unpaid") {
      if ((b.payment?.total_collected || 0) > 0) return false;
      if (b.payment?.deposit_waived) return false;
    } else if (payFilter && b.payment?.payment_status !== payFilter) return false;
    return true;
  });

  const PayBadge = ({ ps }) => (
    <span data-testid={`payment-status-badge`}
      className={`status-pill ${PAYMENT_STATUS_COLORS[ps] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/40"}`}>
      {ps}
    </span>
  );

  return (
    <div>
      <h1 className="font-display text-4xl mb-4">Booking Requests</h1>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="admin-payment-summary">
          <div className="card-surface p-4 border-l-4 border-l-[#CCFF00]">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400">Collected Today</div>
            <div className="font-display text-2xl mt-1" data-testid="summary-today">{fmt$(summary.today)}</div>
          </div>
          <div className="card-surface p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400">This Week</div>
            <div className="font-display text-2xl mt-1" data-testid="summary-week">{fmt$(summary.week)}</div>
          </div>
          <div className="card-surface p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400">This Month</div>
            <div className="font-display text-2xl mt-1" data-testid="summary-month">{fmt$(summary.month)}</div>
          </div>
          <div className="card-surface p-4 border-l-4 border-l-orange-400">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400">Outstanding</div>
            <div className="font-display text-2xl mt-1 text-orange-300" data-testid="summary-outstanding">{fmt$(summary.outstanding)}</div>
            <div className="text-[10px] text-zinc-500 mt-1">{summary.today_appointments} appts today</div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <select data-testid="filter-appt-status" value={apptFilter} onChange={(e) => setApptFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-zinc-700 text-sm bg-transparent">
          {APPT_STATUS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <select data-testid="filter-payment-status" value={payFilter} onChange={(e) => setPayFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-zinc-700 text-sm bg-transparent">
          {PAYMENT_STATUS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <div className="ml-auto text-xs text-zinc-500 self-center">Showing {filtered.length} of {rows.length}</div>
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && <div className="text-zinc-500 text-sm">No bookings match the current filters.</div>}
        {filtered.map((b) => {
          const p = b.payment || {};
          const isOpen = !!expanded[b.id];
          const hist = history[b.id];
          return (
          <div key={b.id} data-testid={`booking-row-${b.id}`} className="card-surface p-5">
            <div className="flex flex-wrap justify-between gap-3 items-start">
              <div className="min-w-0">
                <div className="font-display text-xl">{b.customer_name}</div>
                <div className="text-sm text-zinc-400">{b.totals?.service_name} · {b.vehicle_size} · {b.condition}</div>
                <div className="text-xs text-zinc-500 mt-1">
                  {b.confirmed_date || b.preferred_date} at {b.confirmed_time || b.preferred_time} · {b.address}
                </div>
                {b.assigned_employees && b.assigned_employees.length > 0 && (
                  <div className="flex items-center flex-wrap gap-1.5 mt-1">
                    <span className="text-xs text-[#CCFF00]">Assigned:</span>
                    {b.assigned_employees.map((e) => (
                      <span key={e.id} className="inline-flex items-center gap-1 text-xs bg-[#CCFF00]/10 border border-[#CCFF00]/30 rounded-full px-2 py-0.5">
                        <Avatar src={e.photo_data_uri} name={e.name} size={14} />
                        <span className="text-[#CCFF00]">{e.name}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="flex flex-wrap gap-1.5 justify-end">
                  <span className={`status-pill ${STATUS_COLORS[b.status] || ""}`} data-testid={`appt-status-badge-${b.id}`}>{b.status}</span>
                  {p.payment_status && p.payment_status !== b.status && <PayBadge ps={p.payment_status} />}
                </div>
                <div className="text-xs text-zinc-400 mt-2">Total {fmt$(p.service_total || b.totals?.total)}</div>
                <div className="text-xs text-zinc-300">Balance {fmt$(p.balance_due)}</div>
                {b.flagged_review && <div className="text-xs text-orange-300 mt-1">Flagged review</div>}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {b.status === "Pending Review" && (
                <button data-testid={`assign-btn-${b.id}`} onClick={() => openAssign(b)} className="neon-btn px-4 py-2 rounded-lg text-xs">
                  Approve & schedule
                </button>
              )}
              {b.status === "Awaiting Deposit" && !p.deposit_waived && (
                <button data-testid={`waive-deposit-btn-${b.id}`} onClick={() => openWaive(b.id)}
                  className="border border-fuchsia-500/50 text-fuchsia-300 hover:bg-fuchsia-500/10 px-4 py-2 rounded-lg text-xs uppercase tracking-wider">
                  Waive Deposit
                </button>
              )}
              {b.status !== "Cancelled" && p.payment_status !== "Paid in Full" && (
                <button data-testid={`record-payment-btn-${b.id}`} onClick={() => openRecord(b.id)}
                  className="border border-[#CCFF00]/60 text-[#CCFF00] hover:bg-[#CCFF00]/10 px-4 py-2 rounded-lg text-xs uppercase tracking-wider inline-flex items-center gap-1">
                  <Receipt size={13} /> Record Payment
                </button>
              )}
              {["Confirmed", "Awaiting Deposit"].includes(b.status) && (
                <button onClick={() => updateStatus(b.id, "In Progress")} className="border border-zinc-700 hover:border-[#CCFF00] px-4 py-2 rounded-lg text-xs uppercase tracking-wider">Start job</button>
              )}
              {b.status === "In Progress" && (
                <button onClick={() => updateStatus(b.id, "Completed")} className="border border-zinc-700 hover:border-[#CCFF00] px-4 py-2 rounded-lg text-xs uppercase tracking-wider">Mark completed</button>
              )}
              {b.status !== "Cancelled" && b.status !== "Paid" && (
                <button onClick={() => updateStatus(b.id, "Cancelled")} className="border border-zinc-700 hover:border-red-500 text-red-300 px-4 py-2 rounded-lg text-xs uppercase tracking-wider">Cancel</button>
              )}
              <button data-testid={`toggle-payments-${b.id}`} onClick={() => toggleExpand(b.id)}
                className="ml-auto text-xs text-zinc-400 hover:text-[#CCFF00] inline-flex items-center gap-1">
                {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Payment details
              </button>
            </div>

            {isOpen && (
              <div className="mt-4 border-t border-zinc-800 pt-4 space-y-4" data-testid={`payment-details-${b.id}`}>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#CCFF00] mb-2">Payment Details</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><div className="text-[10px] uppercase text-zinc-500">Service Total</div><div>{fmt$(p.service_total)}</div></div>
                    <div><div className="text-[10px] uppercase text-zinc-500">Deposit Required</div><div>{fmt$(p.deposit_required)}</div></div>
                    <div><div className="text-[10px] uppercase text-zinc-500">Total Collected</div><div data-testid={`total-collected-${b.id}`}>{fmt$(p.total_collected)}</div></div>
                    <div><div className="text-[10px] uppercase text-zinc-500">Balance Due</div><div data-testid={`balance-due-${b.id}`}>{fmt$(p.balance_due)}</div></div>
                  </div>
                  {p.overpayment > 0 && (
                    <div className="mt-2 text-xs text-orange-300">Overpayment: {fmt$(p.overpayment)}</div>
                  )}
                  {p.deposit_waived && (
                    <div className="mt-2 text-xs text-fuchsia-300">Deposit waived
                      {b.deposit_waived_by_name ? ` by ${b.deposit_waived_by_name}` : ""}
                      {b.deposit_waived_at ? ` on ${new Date(b.deposit_waived_at).toLocaleDateString()}` : ""}
                      {b.deposit_waived_reason ? ` — ${b.deposit_waived_reason}` : ""}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#CCFF00] mb-2">Payment History</div>
                  {(!hist || (hist.events || []).length === 0) && (
                    <div className="text-zinc-500 text-xs">No payment events yet.</div>
                  )}
                  <div className="space-y-1.5">
                    {hist && (hist.events || []).slice().reverse().map((ev) => {
                      const mp = ev.kind === "manual_payment" && (hist.manual_payments || []).find((x) => x.id === ev.payment_id);
                      const isVoid = mp && mp.voided;
                      const isVoidEvent = ev.kind === "payment_voided";
                      return (
                        <div key={ev.id} className="flex items-center justify-between gap-3 text-xs bg-black/40 border border-zinc-800 rounded-lg px-3 py-2">
                          <div className="flex-1 min-w-0">
                            <div className={isVoid ? "text-zinc-500 line-through" : ""}>
                              {ev.at ? new Date(ev.at).toLocaleDateString() : ""} —{" "}
                              {ev.kind === "manual_payment" && <>{fmt$(ev.amount)} — {ev.method}{ev.method_other ? ` (${ev.method_other})` : ""} — {ev.actor_name}</>}
                              {ev.kind === "stripe_deposit" && <>{fmt$(ev.amount)} — Stripe — Deposit</>}
                              {ev.kind === "stripe_balance" && <>{fmt$(ev.amount)} — Stripe — Balance</>}
                              {ev.kind === "stripe_tip" && <>{fmt$(ev.amount)} — Stripe — Tip</>}
                              {ev.kind === "deposit_waived" && <>Deposit waived — {ev.actor_name}{ev.reason ? ` (${ev.reason})` : ""}</>}
                              {ev.kind === "paid_in_full" && <>Paid in Full</>}
                              {isVoidEvent && <>VOIDED — {fmt$(-ev.amount)} — {ev.actor_name}{ev.reason ? ` (${ev.reason})` : ""}</>}
                            </div>
                            {ev.note && !isVoid && <div className="text-zinc-500 text-[10px]">Note: {ev.note}</div>}
                            {isVoid && <div className="text-red-400 text-[10px]">VOID{mp.void_reason ? ` — ${mp.void_reason}` : ""}</div>}
                          </div>
                          {ev.kind === "manual_payment" && !isVoid && (
                            <button data-testid={`void-payment-${ev.payment_id}`} onClick={() => voidPayment(b.id, ev.payment_id)}
                              className="text-red-300 hover:text-red-400 text-[10px] uppercase tracking-widest inline-flex items-center gap-1">
                              <Ban size={11} /> Void
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-surface p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="font-display text-2xl mb-1">Approve & schedule</div>
            <div className="text-xs text-zinc-400 mb-4">{modal.customer_name} · {modal.totals?.service_name}</div>
            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-2">
              Detailers ({assign.employee_ids.length} selected)
            </div>
            <div className="space-y-2 mb-3 max-h-48 overflow-y-auto pr-1">
              {detailers.length === 0 && <div className="text-zinc-500 text-sm">No detailers available.</div>}
              {detailers.map((e) => {
                const checked = assign.employee_ids.includes(e.id);
                return (
                  <label key={e.id} data-testid={`assign-checkbox-${e.id}`}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2 cursor-pointer ${checked ? "border-[#CCFF00] bg-[#CCFF00]/10" : "border-zinc-700 hover:border-zinc-500"}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleDetailer(e.id)} className="accent-[#CCFF00]" />
                    <Avatar src={e.photo_data_uri} name={e.name} size={28} />
                    <span className="flex-1 text-sm">{e.name}
                      {e.role === "admin" && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#CCFF00]/80">Admin</span>}
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="flex gap-2 mb-3">
              <button onClick={() => setAssign({ ...assign, accept_time: true })}
                className={`flex-1 p-3 rounded-xl border text-sm ${assign.accept_time ? "border-[#CCFF00] bg-[#CCFF00]/10" : "border-zinc-700"}`}
                data-testid="btn-accept-time">Accept requested time</button>
              <button onClick={() => setAssign({ ...assign, accept_time: false })}
                className={`flex-1 p-3 rounded-xl border text-sm ${!assign.accept_time ? "border-[#CCFF00] bg-[#CCFF00]/10" : "border-zinc-700"}`}
                data-testid="btn-propose-time">Propose new time</button>
            </div>
            {!assign.accept_time && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <input type="date" data-testid="propose-date" value={assign.proposed_date} onChange={(e) => setAssign({ ...assign, proposed_date: e.target.value })}
                  className="px-3 py-2 rounded-xl border border-zinc-700" />
                <input type="time" data-testid="propose-time" value={assign.proposed_time} onChange={(e) => setAssign({ ...assign, proposed_time: e.target.value })}
                  className="px-3 py-2 rounded-xl border border-zinc-700" />
              </div>
            )}
            <button onClick={() => setModal(null)} className="w-full border border-zinc-700 py-2 rounded-xl text-xs mb-3">Cancel</button>
            {assign.accept_time ? (
              <div className="grid grid-cols-1 gap-2">
                <button data-testid="approve-request-deposit-btn" onClick={() => submitAssign(false)}
                  className="neon-btn py-3 rounded-xl text-sm">
                  Approve & Request Deposit ({fmt$(modal.totals?.deposit)})
                </button>
                <button data-testid="approve-waive-deposit-btn" onClick={() => submitAssign(true)}
                  className="py-3 rounded-xl border border-fuchsia-500/60 text-fuchsia-300 hover:bg-fuchsia-500/10 text-sm">
                  Approve & Waive Deposit
                </button>
                <div className="text-[10px] text-zinc-500 text-center">Waiving skips Stripe — the appointment is confirmed and payment can be collected later.</div>
              </div>
            ) : (
              <button data-testid="submit-assign-btn" onClick={() => submitAssign(false)} className="neon-btn w-full py-3 rounded-xl text-sm">Propose new time</button>
            )}
          </div>
        </div>
      )}

      {waiveFor && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-surface p-6 max-w-md w-full">
            <div className="font-display text-2xl mb-3">Waive Deposit</div>
            <p className="text-sm text-zinc-400 mb-3">This will confirm the appointment without requiring a deposit. The customer will get a confirmation email with no deposit language.</p>
            <textarea placeholder="Optional reason (audit trail)" value={waiveReason} onChange={(e) => setWaiveReason(e.target.value)}
              data-testid="waive-reason-input"
              className="w-full px-3 py-2 rounded-xl border border-zinc-700 mb-3 text-sm bg-transparent" rows={2} />
            <div className="flex gap-2">
              <button onClick={() => setWaiveFor(null)} className="flex-1 border border-zinc-700 py-3 rounded-xl">Cancel</button>
              <button data-testid="confirm-waive-btn" onClick={confirmWaive}
                className="flex-1 py-3 rounded-xl border border-fuchsia-500/60 text-fuchsia-300 hover:bg-fuchsia-500/10">
                Waive & Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {recordFor && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-surface p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="font-display text-2xl mb-1">Record Payment</div>
            <div className="text-xs text-zinc-400 mb-4">Cash, Zelle, Card, or any offline payment. This does NOT charge Stripe.</div>
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Amount Collected</div>
                <input type="number" data-testid="record-amount" value={record.amount}
                  onChange={(e) => setRecord({ ...record, amount: e.target.value })}
                  placeholder="0.00" step="0.01"
                  className="w-full px-3 py-3 rounded-xl border border-zinc-700 bg-transparent" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Payment Method</div>
                <div className="grid grid-cols-3 gap-2">
                  {["Cash", "Card", "Zelle", "Cash App", "Other"].map((m) => (
                    <button key={m} data-testid={`method-${m.replace(" ", "-")}`}
                      onClick={() => setRecord({ ...record, method: m })}
                      className={`py-2 rounded-lg border text-xs uppercase tracking-wider ${record.method === m ? "border-[#CCFF00] bg-[#CCFF00]/10 text-[#CCFF00]" : "border-zinc-700"}`}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              {record.method === "Other" && (
                <input placeholder="Describe method (e.g., Venmo, Check)" value={record.method_other}
                  data-testid="record-method-other"
                  onChange={(e) => setRecord({ ...record, method_other: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-zinc-700 bg-transparent" />
              )}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Note (optional)</div>
                <textarea value={record.note} data-testid="record-note"
                  onChange={(e) => setRecord({ ...record, note: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-zinc-700 bg-transparent text-sm" rows={2} />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setRecordFor(null)} className="flex-1 border border-zinc-700 py-3 rounded-xl">Cancel</button>
              <button data-testid="submit-record-payment" onClick={submitRecord} className="neon-btn flex-1 py-3 rounded-xl">Save Payment</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ServicesAdmin() {
  const [rows, setRows] = useState([]);
  const [addons, setAddons] = useState([]);
  const [editing, setEditing] = useState(null);      // service edit modal
  const [editingAddon, setEditingAddon] = useState(null); // addon edit modal
  const [showInactive, setShowInactive] = useState(false);

  const load = () => {
    api.get("/admin/services").then((r) => setRows(r.data));
    api.get("/admin/addons").then((r) => setAddons(r.data));
  };
  useEffect(() => { load(); }, []);

  const emptyService = () => ({
    id: null, name: "", description: "", duration_minutes: 60, deposit_percent: 10,
    promo_active: false, active: true,
    sizes: [
      { size: "Sedan", regular_price: 0, promo_price: null },
      { size: "SUV", regular_price: 0, promo_price: null },
      { size: "Truck", regular_price: 0, promo_price: null },
    ],
  });

  const openEditService = (s) => setEditing({ ...s, sizes: s.sizes.map(sz => ({ ...sz })) });
  const setEd = (patch) => setEditing({ ...editing, ...patch });
  const setSize = (idx, patch) => {
    const sizes = editing.sizes.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    setEd({ sizes });
  };
  const addSize = () => setEd({ sizes: [...editing.sizes, { size: "", regular_price: 0, promo_price: null }] });
  const removeSize = (idx) => setEd({ sizes: editing.sizes.filter((_, i) => i !== idx) });

  const saveService = async () => {
    if (!editing.name.trim()) { toast.error("Name is required"); return; }
    const cleanSizes = editing.sizes
      .filter((s) => s.size && s.size.trim())
      .map((s) => ({
        size: s.size.trim(),
        regular_price: Number(s.regular_price) || 0,
        promo_price: s.promo_price === null || s.promo_price === "" ? null : Number(s.promo_price),
      }));
    if (cleanSizes.length === 0) { toast.error("Add at least one size row"); return; }
    const payload = {
      name: editing.name.trim(), description: editing.description || "",
      duration_minutes: Number(editing.duration_minutes) || 60,
      deposit_percent: Number(editing.deposit_percent) || 0,
      promo_active: !!editing.promo_active, active: !!editing.active,
      sizes: cleanSizes,
    };
    try {
      if (editing.id) await api.put(`/admin/services/${editing.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success(editing.id ? "Service updated" : "Service created");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const deleteService = async (s) => {
    if (!window.confirm(`Deactivate "${s.name}"? Existing bookings are unaffected; customers won't see it on the site anymore.`)) return;
    try { await api.delete(`/admin/services/${s.id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const reactivateService = async (s) => {
    try { await api.put(`/admin/services/${s.id}`, { ...s, active: true }); toast.success("Reactivated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const togglePromo = async (svc) => {
    try { await api.put(`/admin/services/${svc.id}`, { ...svc, promo_active: !svc.promo_active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  // Add-ons
  const saveAddon = async () => {
    if (!editingAddon.name.trim()) { toast.error("Name is required"); return; }
    const payload = {
      name: editingAddon.name.trim(),
      price: Number(editingAddon.price) || 0,
      active: !!editingAddon.active,
    };
    try {
      if (editingAddon.id) await api.put(`/admin/addons/${editingAddon.id}`, payload);
      else await api.post("/admin/addons", payload);
      toast.success(editingAddon.id ? "Add-on updated" : "Add-on created");
      setEditingAddon(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const deleteAddon = async (a) => {
    if (!window.confirm(`Deactivate "${a.name}" add-on?`)) return;
    try { await api.delete(`/admin/addons/${a.id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const visibleServices = showInactive ? rows : rows.filter((s) => s.active !== false);
  const visibleAddons = showInactive ? addons : addons.filter((a) => a.active !== false);

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-display text-4xl">Services & Pricing</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-300 px-3 py-2 rounded-lg border border-zinc-700 cursor-pointer hover:border-[#CCFF00]">
            <input type="checkbox" data-testid="show-inactive-toggle"
              checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
              className="accent-[#CCFF00]" />
            Show inactive
          </label>
          <button data-testid="add-service-btn" onClick={() => setEditing(emptyService())}
            className="neon-btn px-4 py-2 rounded-xl text-sm">+ Add service</button>
        </div>
      </div>

      <div className="space-y-3">
        {visibleServices.length === 0 && <div className="text-zinc-500 text-sm">No services yet. Click "Add service" to create one.</div>}
        {visibleServices.map((s) => (
          <div key={s.id} data-testid={`service-row-${s.id}`}
            className={`card-surface p-5 ${s.active === false ? "opacity-60" : ""}`}>
            <div className="flex justify-between items-start flex-wrap gap-3">
              <div>
                <div className="font-display text-xl flex items-center gap-2">
                  {s.name}
                  {s.active === false && (
                    <span className="text-[10px] uppercase tracking-widest text-zinc-400 border border-zinc-700 rounded-full px-2 py-0.5">Inactive</span>
                  )}
                </div>
                <div className="text-sm text-zinc-400">{s.description}</div>
                <div className="text-xs text-zinc-500 mt-1">{s.duration_minutes} min · {s.deposit_percent}% deposit</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button data-testid={`toggle-promo-${s.id}`} onClick={() => togglePromo(s)}
                  className={`px-3 py-2 rounded-lg text-xs uppercase tracking-widest border ${s.promo_active ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-zinc-700"}`}>
                  Promo {s.promo_active ? "ON" : "OFF"}
                </button>
                <button data-testid={`edit-service-${s.id}`} onClick={() => openEditService(s)}
                  className="border border-zinc-700 hover:border-[#CCFF00] hover:text-[#CCFF00] px-3 py-2 rounded-lg text-xs uppercase tracking-widest">
                  Edit
                </button>
                {s.active === false ? (
                  <button data-testid={`reactivate-service-${s.id}`} onClick={() => reactivateService(s)}
                    className="border border-zinc-700 hover:border-[#CCFF00] hover:text-[#CCFF00] px-3 py-2 rounded-lg text-xs uppercase tracking-widest">
                    Reactivate
                  </button>
                ) : (
                  <button data-testid={`delete-service-${s.id}`} onClick={() => deleteService(s)}
                    className="border border-zinc-700 hover:border-red-500 hover:text-red-300 px-3 py-2 rounded-lg text-xs uppercase tracking-widest">
                    Delete
                  </button>
                )}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
              {s.sizes.map((sz) => (
                <div key={sz.size} className="bg-black/40 rounded-lg p-2">
                  <div className="text-xs text-zinc-500">{sz.size}</div>
                  <div>
                    {fmt$(sz.regular_price)}
                    {sz.promo_price ? <span className="text-[#CCFF00]"> · promo {fmt$(sz.promo_price)}</span> : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Add-ons */}
      <div className="mt-10 flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-display text-3xl">Add-ons</h2>
        <button data-testid="add-addon-btn"
          onClick={() => setEditingAddon({ id: null, name: "", price: 0, active: true })}
          className="neon-btn px-4 py-2 rounded-xl text-sm">+ Add add-on</button>
      </div>
      <div className="space-y-2">
        {visibleAddons.length === 0 && <div className="text-zinc-500 text-sm">No add-ons yet.</div>}
        {visibleAddons.map((a) => (
          <div key={a.id} data-testid={`addon-row-${a.id}`}
            className={`card-surface p-4 flex justify-between items-center flex-wrap gap-3 ${a.active === false ? "opacity-60" : ""}`}>
            <div>
              <div className="font-semibold flex items-center gap-2">
                {a.name}
                {a.active === false && (
                  <span className="text-[10px] uppercase tracking-widest text-zinc-400 border border-zinc-700 rounded-full px-2 py-0.5">Inactive</span>
                )}
              </div>
              <div className="text-xs text-zinc-400">+{fmt$(a.price)}</div>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid={`edit-addon-${a.id}`} onClick={() => setEditingAddon({ ...a })}
                className="border border-zinc-700 hover:border-[#CCFF00] hover:text-[#CCFF00] px-3 py-1.5 rounded-lg text-xs uppercase tracking-widest">
                Edit
              </button>
              <button data-testid={`delete-addon-${a.id}`} onClick={() => deleteAddon(a)}
                className="border border-zinc-700 hover:border-red-500 hover:text-red-300 px-3 py-1.5 rounded-lg text-xs uppercase tracking-widest">
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
          data-testid="service-edit-modal">
          <div className="card-surface p-6 max-w-2xl w-full my-8">
            <div className="flex justify-between items-center mb-4">
              <div className="font-display text-2xl">{editing.id ? "Edit service" : "New service"}</div>
              <button onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white"><XIcon /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="svc-name" placeholder="Name" value={editing.name}
                onChange={(e) => setEd({ name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-zinc-700" />
              <textarea data-testid="svc-description" placeholder="Description" value={editing.description}
                onChange={(e) => setEd({ description: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-zinc-700 min-h-[70px]" />
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs uppercase tracking-widest text-zinc-400 flex flex-col gap-1">
                  Duration (minutes)
                  <input data-testid="svc-duration" type="number" min="1" value={editing.duration_minutes}
                    onChange={(e) => setEd({ duration_minutes: e.target.value })}
                    className="px-3 py-2 rounded-lg border border-zinc-700 text-white text-sm normal-case tracking-normal" />
                </label>
                <label className="text-xs uppercase tracking-widest text-zinc-400 flex flex-col gap-1">
                  Deposit percent
                  <input data-testid="svc-deposit" type="number" min="0" max="100" value={editing.deposit_percent}
                    onChange={(e) => setEd({ deposit_percent: e.target.value })}
                    className="px-3 py-2 rounded-lg border border-zinc-700 text-white text-sm normal-case tracking-normal" />
                </label>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="svc-promo-active" checked={editing.promo_active}
                    onChange={(e) => setEd({ promo_active: e.target.checked })} />
                  Promo pricing active
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="svc-active" checked={editing.active}
                    onChange={(e) => setEd({ active: e.target.checked })} />
                  Visible on site
                </label>
              </div>

              <div className="mt-2">
                <div className="text-xs uppercase tracking-widest text-[#CCFF00] mb-2">Pricing by size</div>
                <div className="space-y-2">
                  {editing.sizes.map((sz, i) => (
                    <div key={i} data-testid={`svc-size-row-${i}`}
                      className="grid grid-cols-12 gap-2 items-center">
                      <input placeholder="Size" value={sz.size}
                        onChange={(e) => setSize(i, { size: e.target.value })}
                        className="col-span-4 px-3 py-2 rounded-lg border border-zinc-700 text-sm" />
                      <input type="number" step="0.01" placeholder="Regular"
                        value={sz.regular_price}
                        onChange={(e) => setSize(i, { regular_price: e.target.value })}
                        className="col-span-3 px-3 py-2 rounded-lg border border-zinc-700 text-sm" />
                      <input type="number" step="0.01" placeholder="Promo (opt)"
                        value={sz.promo_price ?? ""}
                        onChange={(e) => setSize(i, { promo_price: e.target.value })}
                        className="col-span-4 px-3 py-2 rounded-lg border border-zinc-700 text-sm" />
                      {editing.sizes.length > 1 && (
                        <button onClick={() => removeSize(i)}
                          className="col-span-1 text-red-300 hover:text-red-200 text-xs">✕</button>
                      )}
                    </div>
                  ))}
                </div>
                <button data-testid="svc-add-size-btn" onClick={addSize}
                  className="mt-2 text-xs uppercase tracking-widest border border-zinc-700 hover:border-[#CCFF00] hover:text-[#CCFF00] rounded-lg px-3 py-2">
                  + Add size
                </button>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="flex-1 border border-zinc-700 py-3 rounded-xl">Cancel</button>
              <button data-testid="svc-save-btn" onClick={saveService} className="neon-btn flex-1 py-3 rounded-xl">
                {editing.id ? "Save" : "Create service"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingAddon && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="addon-edit-modal">
          <div className="card-surface p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <div className="font-display text-2xl">{editingAddon.id ? "Edit add-on" : "New add-on"}</div>
              <button onClick={() => setEditingAddon(null)} className="text-zinc-400 hover:text-white"><XIcon /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="addon-name" placeholder="Name" value={editingAddon.name}
                onChange={(e) => setEditingAddon({ ...editingAddon, name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-zinc-700" />
              <input data-testid="addon-price" type="number" step="0.01" placeholder="Price"
                value={editingAddon.price}
                onChange={(e) => setEditingAddon({ ...editingAddon, price: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-zinc-700" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="addon-active" checked={editingAddon.active}
                  onChange={(e) => setEditingAddon({ ...editingAddon, active: e.target.checked })} />
                Visible on booking form
              </label>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setEditingAddon(null)} className="flex-1 border border-zinc-700 py-3 rounded-xl">Cancel</button>
              <button data-testid="addon-save-btn" onClick={saveAddon} className="neon-btn flex-1 py-3 rounded-xl">
                {editingAddon.id ? "Save" : "Create add-on"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Employees() {
  const [rows, setRows] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [role, setRole] = useState("employee"); // "employee" | "admin"
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "Detailer!2026", is_detailer: false });
  const [edit, setEdit] = useState(null); // user being edited
  const [availUser, setAvailUser] = useState(null); // user whose availability is being edited
  const currentUser = loadUser();
  const load = () => {
    api.get("/admin/employees").then((r) => setRows(r.data));
    api.get("/admin/admins").then((r) => setAdmins(r.data));
  };
  useEffect(() => { load(); }, []);
  const switchRole = (r) => {
    setRole(r);
    setF({ ...f, password: r === "admin" ? "Admin!2026" : "Detailer!2026", is_detailer: false });
  };
  const add = async () => {
    if (!f.name || !f.email) { toast.error("Name and email are required"); return; }
    try {
      await api.post("/admin/employees", { ...f, role, is_detailer: role === "admin" ? !!f.is_detailer : false });
      toast.success(role === "admin" ? "Admin added" : "Detailer added");
      setF({ name: "", email: "", phone: "", password: role === "admin" ? "Admin!2026" : "Detailer!2026", is_detailer: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const openEdit = (u) => setEdit({
    id: u.id, name: u.name || "", email: u.email || "",
    phone: u.phone || "", active: u.active !== false,
    role: u.role || "employee", password: "",
    photo_data_uri: u.photo_data_uri || null,
    notify_new_bookings: u.notify_new_bookings !== false,
    is_detailer: !!u.is_detailer,
  });
  const saveEdit = async () => {
    if (!edit.name || !edit.email) { toast.error("Name and email are required"); return; }
    try {
      const payload = { ...edit };
      const photo = payload.photo_data_uri;
      const initialPhoto = (admins.concat(rows).find((u) => u.id === edit.id) || {}).photo_data_uri || null;
      delete payload.photo_data_uri;
      if (!payload.password) delete payload.password;
      await api.put(`/admin/employees/${edit.id}`, payload);
      // Sync photo separately (only if changed)
      if (photo && photo !== initialPhoto) {
        await api.post(`/admin/employees/${edit.id}/photo`, { data_uri: photo });
      } else if (!photo && initialPhoto) {
        await api.delete(`/admin/employees/${edit.id}/photo`);
      }
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const pickEditPhoto = async (file) => {
    if (!file) return;
    try {
      const data_uri = await fileToCompressedDataUri(file, { maxSize: 512, quality: 0.85 });
      if (data_uri.length > 400_000) { toast.error("Image still too large after compression"); return; }
      setEdit({ ...edit, photo_data_uri: data_uri });
    } catch (e) { toast.error("Could not read image"); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/employees/${u.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const RowActions = ({ u }) => (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="status-pill">{u.active ? "Active" : "Off"}</span>
      {(u.role === "employee" || (u.role === "admin" && u.is_detailer)) && (
        <button data-testid={`availability-user-${u.id}`} onClick={() => setAvailUser(u)}
          className="border border-zinc-700 hover:border-[#CCFF00] hover:text-[#CCFF00] px-3 py-1.5 rounded-lg text-xs uppercase tracking-widest inline-flex items-center gap-1">
          <Clock size={12} /> Availability
        </button>
      )}
      <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)}
        className="border border-zinc-700 hover:border-[#CCFF00] hover:text-[#CCFF00] px-3 py-1.5 rounded-lg text-xs uppercase tracking-widest">
        Edit
      </button>
      {u.id !== currentUser?.id && (
        <button data-testid={`delete-user-${u.id}`} onClick={() => removeUser(u)}
          className="border border-zinc-700 hover:border-red-500 hover:text-red-300 px-3 py-1.5 rounded-lg text-xs uppercase tracking-widest">
          Delete
        </button>
      )}
    </div>
  );

  return (
    <div>
      <h1 className="font-display text-4xl mb-6">Team</h1>

      <div className="card-surface p-5 mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="font-display text-xl mr-2">Add</div>
          <button data-testid="role-toggle-employee" onClick={() => switchRole("employee")}
            className={`px-3 py-2 rounded-lg text-xs uppercase tracking-widest border ${role === "employee" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-zinc-700"}`}>
            Detailer
          </button>
          <button data-testid="role-toggle-admin" onClick={() => switchRole("admin")}
            className={`px-3 py-2 rounded-lg text-xs uppercase tracking-widest border ${role === "admin" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-zinc-700"}`}>
            Admin
          </button>
          {role === "admin" && (
            <span className="ml-2 text-xs text-zinc-400">Admins have full access to the dashboard, bookings, reports and settings.</span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input data-testid="emp-name" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="px-3 py-2 rounded-lg border border-zinc-700" />
          <input data-testid="emp-email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="px-3 py-2 rounded-lg border border-zinc-700" />
          <input data-testid="emp-phone" placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="px-3 py-2 rounded-lg border border-zinc-700" />
          <input data-testid="emp-password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="px-3 py-2 rounded-lg border border-zinc-700" />
        </div>
        {role === "admin" && (
          <label className="flex items-center gap-2 mt-3 text-sm text-zinc-300">
            <input type="checkbox" data-testid="emp-is-detailer" checked={!!f.is_detailer}
              onChange={(e) => setF({ ...f, is_detailer: e.target.checked })} />
            Also works as a detailer (can be assigned to jobs & set availability)
          </label>
        )}
        <button data-testid="emp-add-btn" onClick={add} className="neon-btn mt-3 px-5 py-2 rounded-xl text-sm">
          {role === "admin" ? "Add admin" : "Add detailer"}
        </button>
      </div>

      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-[#CCFF00] mb-2">Admins</div>
        <div className="space-y-2">
          {admins.length === 0 && <div className="text-zinc-500 text-sm">No admins yet.</div>}
          {admins.map((a) => (
            <div key={a.id} data-testid={`admin-row-${a.id}`} className="card-surface p-4 flex justify-between items-center flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Avatar src={a.photo_data_uri} name={a.name} size={42} />
                <div>
                  <div className="font-semibold flex items-center gap-2 flex-wrap">
                    {a.name}
                    <span className="text-[10px] uppercase tracking-widest text-[#CCFF00] bg-[#CCFF00]/10 border border-[#CCFF00]/40 rounded-full px-2 py-0.5">Admin</span>
                    {a.is_detailer && (
                      <span data-testid={`admin-detailer-badge-${a.id}`}
                        className="text-[10px] uppercase tracking-widest text-purple-300 bg-purple-500/10 border border-purple-500/40 rounded-full px-2 py-0.5">Detailer</span>
                    )}
                    {a.id === currentUser?.id && (
                      <span className="text-[10px] uppercase tracking-widest text-zinc-400 border border-zinc-700 rounded-full px-2 py-0.5">You</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-400">{a.email}{a.phone ? ` · ${a.phone}` : ""}</div>
                </div>
              </div>
              <RowActions u={a} />
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-400 mb-2">Detailers</div>
        <div className="space-y-2">
          {rows.length === 0 && <div className="text-zinc-500 text-sm">No detailers yet.</div>}
          {rows.map((e) => (
            <div key={e.id} data-testid={`detailer-row-${e.id}`} className="card-surface p-4 flex justify-between items-center flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Avatar src={e.photo_data_uri} name={e.name} size={42} />
                <div>
                  <div className="font-semibold">{e.name}</div>
                  <div className="text-xs text-zinc-400">{e.email}{e.phone ? ` · ${e.phone}` : ""}</div>
                </div>
              </div>
              <RowActions u={e} />
            </div>
          ))}
        </div>
      </div>

      {edit && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">          <div className="card-surface p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <div className="font-display text-2xl">Edit {edit.role === "admin" ? "admin" : "detailer"}</div>
              <button onClick={() => setEdit(null)} className="text-zinc-400 hover:text-white"><XIcon /></button>
            </div>
            <div className="flex items-center gap-4 mb-4">
              <Avatar src={edit.photo_data_uri} name={edit.name} size={64} />
              <div className="flex flex-col gap-2">
                <label data-testid="edit-photo-upload" className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-700 hover:border-[#CCFF00] hover:text-[#CCFF00] text-xs uppercase tracking-widest">
                  <Camera size={14} /> {edit.photo_data_uri ? "Change photo" : "Upload photo"}
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => pickEditPhoto(e.target.files?.[0])} />
                </label>
                {edit.photo_data_uri && (
                  <button data-testid="edit-photo-remove" onClick={() => setEdit({ ...edit, photo_data_uri: null })}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-700 hover:border-red-500 hover:text-red-300 text-xs uppercase tracking-widest">
                    <Trash2 size={14} /> Remove
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-2">
              <input data-testid="edit-name" placeholder="Name" value={edit.name}
                onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                className="px-3 py-2 rounded-lg border border-zinc-700" />
              <input data-testid="edit-email" placeholder="Email" value={edit.email}
                onChange={(e) => setEdit({ ...edit, email: e.target.value })}
                className="px-3 py-2 rounded-lg border border-zinc-700" />
              <input data-testid="edit-phone" placeholder="Phone" value={edit.phone}
                onChange={(e) => setEdit({ ...edit, phone: e.target.value })}
                className="px-3 py-2 rounded-lg border border-zinc-700" />
              <input data-testid="edit-password" placeholder="New password (leave blank to keep current)"
                value={edit.password}
                onChange={(e) => setEdit({ ...edit, password: e.target.value })}
                className="px-3 py-2 rounded-lg border border-zinc-700" />
              <div className="flex gap-2 mt-2">
                <button data-testid="edit-role-employee" onClick={() => setEdit({ ...edit, role: "employee" })}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs uppercase tracking-widest border ${edit.role === "employee" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-zinc-700"}`}>
                  Detailer
                </button>
                <button data-testid="edit-role-admin" onClick={() => setEdit({ ...edit, role: "admin" })}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs uppercase tracking-widest border ${edit.role === "admin" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-zinc-700"}`}>
                  Admin
                </button>
              </div>
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input type="checkbox" data-testid="edit-active" checked={edit.active}
                  onChange={(e) => setEdit({ ...edit, active: e.target.checked })} />
                Active (can sign in)
              </label>
              {edit.role === "admin" && (
                <>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" data-testid="edit-notify" checked={edit.notify_new_bookings}
                      onChange={(e) => setEdit({ ...edit, notify_new_bookings: e.target.checked })} />
                    Email me new bookings and group inquiries
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" data-testid="edit-is-detailer" checked={!!edit.is_detailer}
                      onChange={(e) => setEdit({ ...edit, is_detailer: e.target.checked })} />
                    Also works as a detailer (can be assigned to jobs & set availability)
                  </label>
                </>
              )}
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setEdit(null)} className="flex-1 border border-zinc-700 py-3 rounded-xl">Cancel</button>
              <button data-testid="edit-save-btn" onClick={saveEdit} className="neon-btn flex-1 py-3 rounded-xl">Save</button>
            </div>
          </div>
        </div>
      )}
      {availUser && (
        <AvailabilityEditorModal user={availUser} onClose={() => setAvailUser(null)} />
      )}
    </div>
  );
}

function AvailabilityEditorModal({ user, onClose }) {
  const [weekly, setWeekly] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [newException, setNewException] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/admin/employees/${user.id}/availability`).then((r) => {
      setWeekly(r.data.weekly || []);
      setExceptions(r.data.exceptions || []);
      setLoaded(true);
    });
  }, [user.id]);

  const dayFor = (idx) => weekly.find((x) => x.weekday === idx);
  const setDay = (idx, patch) => {
    const w = [...weekly];
    const existing = w.findIndex((x) => x.weekday === idx);
    if (existing >= 0) w[existing] = { ...w[existing], ...patch };
    else w.push({ weekday: idx, start: "09:00", end: "17:00", ...patch });
    setWeekly(w);
  };
  const removeDay = (idx) => setWeekly(weekly.filter((x) => x.weekday !== idx));
  const addException = () => {
    if (!newException) return;
    if (exceptions.some((x) => x.date === newException)) return toast.error("Date already added");
    setExceptions([...exceptions, { date: newException, available: false }]);
    setNewException("");
  };
  const removeException = (date) => setExceptions(exceptions.filter((x) => x.date !== date));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/employees/${user.id}/availability`, { weekly, exceptions });
      toast.success("Availability saved");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="availability-modal">
      <div className="card-surface p-6 max-w-lg w-full my-8">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <Avatar src={user.photo_data_uri} name={user.name} size={40} />
            <div>
              <div className="font-display text-2xl">{user.name}'s hours</div>
              <div className="text-xs text-zinc-400">{user.email}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><XIcon /></button>
        </div>

        {!loaded ? (
          <div className="text-zinc-400 text-sm">Loading...</div>
        ) : (
          <>
            <div className="text-xs uppercase tracking-widest text-[#CCFF00] mb-3">Weekly hours</div>
            <div className="space-y-2 mb-6">
              {DAYS.map((d, i) => {
                const on = !!dayFor(i);
                const cur = dayFor(i) || { start: "09:00", end: "17:00" };
                return (
                  <div key={i} className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 w-20">
                      <input type="checkbox" data-testid={`avail-day-${d.toLowerCase()}`}
                        checked={on}
                        onChange={(e) => e.target.checked ? setDay(i, {}) : removeDay(i)} />
                      <span className="text-sm">{d}</span>
                    </label>
                    <input type="time" value={cur.start} disabled={!on}
                      data-testid={`avail-start-${d.toLowerCase()}`}
                      onChange={(e) => setDay(i, { start: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-zinc-700 disabled:opacity-40" />
                    <input type="time" value={cur.end} disabled={!on}
                      data-testid={`avail-end-${d.toLowerCase()}`}
                      onChange={(e) => setDay(i, { end: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-zinc-700 disabled:opacity-40" />
                  </div>
                );
              })}
            </div>

            <div className="text-xs uppercase tracking-widest text-[#CCFF00] mb-3">Day-off exceptions</div>
            <div className="flex gap-2 mb-3">
              <input type="date" value={newException}
                data-testid="avail-exception-date"
                onChange={(e) => setNewException(e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg border border-zinc-700" />
              <button data-testid="avail-exception-add" onClick={addException}
                className="px-4 py-2 rounded-lg text-xs uppercase tracking-widest border border-zinc-700 hover:border-[#CCFF00] hover:text-[#CCFF00]">
                Add day off
              </button>
            </div>
            <div className="space-y-1 mb-6">
              {exceptions.length === 0 && <div className="text-zinc-500 text-xs">No day-off exceptions.</div>}
              {exceptions.filter((x) => x.available === false).map((x) => (
                <div key={x.date} data-testid={`avail-exception-${x.date}`}
                  className="flex justify-between items-center bg-black/30 rounded-lg px-3 py-2 text-sm">
                  <span className="font-mono">{x.date}</span>
                  <button onClick={() => removeException(x.date)}
                    className="text-xs text-red-300 hover:text-red-200">Remove</button>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 border border-zinc-700 py-3 rounded-xl">Cancel</button>
          <button data-testid="avail-save-btn" disabled={saving || !loaded} onClick={save}
            className="neon-btn flex-1 py-3 rounded-xl">
            {saving ? "Saving..." : "Save availability"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Promos() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ code: "", discount_percent: 10, waives_deposit: false, active: true });
  const load = () => api.get("/admin/promo-codes").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await api.post("/admin/promo-codes", f); toast.success("Created"); load(); }
    catch (e) { toast.error("Error"); }
  };
  return (
    <div>
      <h1 className="font-display text-4xl mb-6">Promo Codes</h1>
      <div className="card-surface p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input data-testid="promo-code" placeholder="Code" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} className="px-3 py-2 rounded-lg border border-zinc-700" />
          <input data-testid="promo-discount" type="number" placeholder="Discount %" value={f.discount_percent} onChange={(e) => setF({ ...f, discount_percent: Number(e.target.value) })} className="px-3 py-2 rounded-lg border border-zinc-700" />
          <label className="flex items-center gap-2 text-sm px-3">
            <input type="checkbox" data-testid="promo-waives" checked={f.waives_deposit} onChange={(e) => setF({ ...f, waives_deposit: e.target.checked })} /> Waives deposit
          </label>
          <button data-testid="promo-add-btn" onClick={add} className="neon-btn rounded-xl text-sm">Add code</button>
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((p) => (
          <div key={p.id} className="card-surface p-4 flex justify-between">
            <div>
              <div className="font-mono font-semibold">{p.code}</div>
              <div className="text-xs text-zinc-400">{p.discount_percent}% off {p.waives_deposit ? "· waives deposit" : ""}</div>
            </div>
            <span className="status-pill">{p.active ? "Active" : "Off"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GroupInquiries() {
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [modal, setModal] = useState(null); // group inquiry being converted
  const [preferred_date, setPreferredDate] = useState("");
  const [items, setItems] = useState([]);

  const load = () => api.get("/admin/group-inquiries").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/services").then((r) => setServices(r.data));
  }, []);

  const openConvert = (g) => {
    setModal(g);
    setPreferredDate(g.preferred_date || "");
    const count = Math.max(1, Number(g.estimated_vehicles || 1));
    const svc = services[0]?.id || "";
    setItems(
      Array.from({ length: count }, () => ({
        service_id: svc, vehicle_size: "Sedan", condition: "Light",
        customer_name: "", customer_email: "", customer_phone: "",
        vehicle_year: "", vehicle_make: "", vehicle_model: "",
        preferred_time: "10:00", notes: "",
      }))
    );
  };

  const updateItem = (idx, patch) => {
    setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const addRow = () => setItems([...items, {
    service_id: services[0]?.id || "", vehicle_size: "Sedan", condition: "Light",
    customer_name: "", customer_email: "", customer_phone: "",
    vehicle_year: "", vehicle_make: "", vehicle_model: "", preferred_time: "10:00", notes: "",
  }]);
  const removeRow = (idx) => setItems(items.filter((_, i) => i !== idx));

  const submitConvert = async () => {
    try {
      const validItems = items.filter((it) => it.customer_name && it.customer_email && it.customer_phone && it.service_id);
      if (validItems.length === 0) return toast.error("Add at least one vehicle with contact info");
      if (!preferred_date) return toast.error("Pick a preferred date");
      const { data } = await api.post(`/admin/group-inquiries/${modal.id}/convert`, {
        preferred_date, items: validItems,
      });
      toast.success(`Created ${data.count} booking${data.count === 1 ? "" : "s"}`);
      setModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div>
      <h1 className="font-display text-4xl mb-6">Group / Partnership Inquiries</h1>
      <div className="space-y-3">
        {rows.length === 0 && <div className="text-zinc-500">No inquiries yet.</div>}
        {rows.map((g) => (
          <div key={g.id} data-testid={`group-row-${g.id}`} className="card-surface p-5">
            <div className="flex justify-between flex-wrap gap-3">
              <div>
                <div className="font-display text-xl">{g.organizer_name}</div>
                <div className="text-sm text-zinc-400">{g.group_type} · {g.estimated_vehicles} vehicles</div>
                <div className="text-xs text-zinc-500 mt-1">{g.location} · {g.email} · {g.phone}</div>
                {g.preferred_date && <div className="text-xs text-zinc-500 mt-1">Preferred: {g.preferred_date}</div>}
              </div>
              <div className="text-right">
                <span className="status-pill">{g.status}</span>
                {g.status !== "converted" && (
                  <button data-testid={`convert-btn-${g.id}`} onClick={() => openConvert(g)}
                    className="mt-2 block neon-btn px-4 py-2 rounded-lg text-xs">
                    Convert to bookings
                  </button>
                )}
                {g.converted_bookings?.length > 0 && (
                  <div className="text-xs text-[#CCFF00] mt-1">{g.converted_bookings.length} bookings created</div>
                )}
              </div>
            </div>
            {g.notes && <div className="text-sm text-zinc-400 mt-3">{g.notes}</div>}
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="card-surface p-6 max-w-3xl w-full my-8">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-display text-2xl">Convert group inquiry</div>
                <div className="text-xs text-zinc-400 mt-1">{modal.organizer_name} · {modal.location}</div>
              </div>
              <button onClick={() => setModal(null)} className="text-zinc-400 hover:text-white"><XIcon /></button>
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <label className="text-xs uppercase tracking-widest text-zinc-400">Shared date</label>
              <input type="date" data-testid="convert-shared-date" value={preferred_date}
                onChange={(e) => setPreferredDate(e.target.value)}
                className="px-3 py-2 rounded-lg border border-zinc-700" />
              <button data-testid="convert-add-row" onClick={addRow}
                className="ml-auto text-xs uppercase tracking-widest border border-zinc-700 hover:border-[#CCFF00] px-3 py-2 rounded-lg">
                + Add vehicle
              </button>
            </div>
            <div className="mt-4 space-y-3 max-h-[55vh] overflow-y-auto pr-1">
              {items.map((it, idx) => (
                <div key={idx} className="rounded-xl border border-zinc-700 p-3 space-y-2" data-testid={`convert-item-${idx}`}>
                  <div className="flex justify-between items-center">
                    <div className="text-xs uppercase tracking-widest text-zinc-500">Vehicle #{idx + 1}</div>
                    {items.length > 1 && (
                      <button onClick={() => removeRow(idx)} className="text-xs text-red-300 hover:text-red-200">Remove</button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <input placeholder="Customer name" value={it.customer_name}
                      onChange={(e) => updateItem(idx, { customer_name: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-zinc-700" />
                    <input placeholder="Email" value={it.customer_email}
                      onChange={(e) => updateItem(idx, { customer_email: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-zinc-700" />
                    <input placeholder="Phone" value={it.customer_phone}
                      onChange={(e) => updateItem(idx, { customer_phone: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-zinc-700" />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <select value={it.service_id} onChange={(e) => updateItem(idx, { service_id: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-zinc-700">
                      {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <select value={it.vehicle_size} onChange={(e) => updateItem(idx, { vehicle_size: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-zinc-700">
                      {["Sedan", "SUV", "Truck"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={it.condition} onChange={(e) => updateItem(idx, { condition: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-zinc-700">
                      {["Light", "Moderate", "Heavy", "Extreme"].map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input type="time" value={it.preferred_time}
                      onChange={(e) => updateItem(idx, { preferred_time: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-zinc-700" />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setModal(null)} className="flex-1 border border-zinc-700 py-3 rounded-xl">Cancel</button>
              <button data-testid="convert-submit-btn" onClick={submitConvert} className="neon-btn flex-1 py-3 rounded-xl">
                Create {items.length} booking{items.length === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminHome() {
  const user = loadUser();
  const nav = useNavigate();
  const [pwModal, setPwModal] = useState(false);
  return (
    <div className="flex bg-[#0A0B0E] min-h-screen text-white">
      <AdminNav />
      <div className="flex-1 p-6 lg:p-10 overflow-x-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="text-sm text-zinc-400">{user?.email}</div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-change-password-btn" onClick={() => setPwModal(true)}
              className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-1">
              <KeyRound size={14} /> Password
            </button>
            <button data-testid="admin-logout-btn" onClick={() => { logout(); nav("/"); }}
              className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-1">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
        <Routes>
          <Route path="" element={<Dashboard />} />
          <Route path="calendar" element={<AdminCalendar />} />
          <Route path="bookings" element={<Bookings />} />
          <Route path="services" element={<ServicesAdmin />} />
          <Route path="employees" element={<Employees />} />
          <Route path="promo-codes" element={<Promos />} />
          <Route path="tip-payouts" element={<TipPayouts />} />
          <Route path="group-inquiries" element={<GroupInquiries />} />
        </Routes>
      </div>
      {pwModal && <ChangePasswordModal onClose={() => setPwModal(false)} />}
    </div>
  );
}

export function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [nu, setNu] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (saving) return;
    if (nu !== confirm) return toast.error("New passwords don't match");
    if (nu.length < 8) return toast.error("Password must be at least 8 characters");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nu });
      toast.success("Password updated");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="change-password-modal">
      <div className="card-surface p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <div className="font-display text-2xl">Change password</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><XIcon /></button>
        </div>
        <div className="space-y-2">
          <input data-testid="cp-current" type="password" placeholder="Current password" value={cur}
            onChange={(e) => setCur(e.target.value)}
            className="w-full px-3 py-3 rounded-lg border border-zinc-700" />
          <input data-testid="cp-new" type="password" placeholder="New password (min 8 chars)" value={nu}
            onChange={(e) => setNu(e.target.value)}
            className="w-full px-3 py-3 rounded-lg border border-zinc-700" />
          <input data-testid="cp-confirm" type="password" placeholder="Confirm new password" value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full px-3 py-3 rounded-lg border border-zinc-700" />
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 border border-zinc-700 py-3 rounded-xl">Cancel</button>
          <button data-testid="cp-save-btn" disabled={saving || !cur || !nu} onClick={submit}
            className="neon-btn flex-1 py-3 rounded-xl">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
